/**
 * Skool365 - Athlete Portal
 *
 * The Athlete Portal focuses on:
 * - Following assigned training programs
 * - Uploading training videos for review
 * - Viewing coach feedback and annotations
 * - Participating in challenges
 * - Tracking workout progress
 * - Creating highlight reels
 *
 * Guardian Mode:
 * - Guardians (parents) can view their linked athletes' progress
 * - Simplified navigation with read-only access
 * - Family management and notification preferences
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  useAuth,
  useUser,
  useToast,
  listOrgs,
  listFamilyLinks,
  validateInvite,
  acceptEmailInvite,
  acceptFamilyInvite,
  joinViaTeamCode,
  config,
  type Organization,
  type ValidatedInvite,
} from '@tss/shared';
import {
  Home,
  Video,
  BookOpen,
  Trophy,
  Film,
  Settings,
  LogOut,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Loader2,
  ArrowRight,
  Users,
  UserCheck,
} from 'lucide-react';

// Import pages
import { HomePage } from './pages/HomePage';
import { MyProgramsPage } from './pages/MyProgramsPage';
import { VideosPage } from './pages/VideosPage';
import { ChallengesPage } from './pages/ChallengesPage';
import { HighlightsPage } from './pages/HighlightsPage';
import { SettingsPage } from './pages/SettingsPage';
import { ProgressPage } from './pages/ProgressPage';
import { GuardianHomePage } from './pages/GuardianHomePage';
import { FamilyPage } from './pages/FamilyPage';
import { AuthModal } from './components/AuthModal';
import { OnboardingChat } from './components/OnboardingChat';
import { OrgSwitcher } from './components/UI';
import { BarChart3 } from 'lucide-react';

type Page =
  | 'home'
  | 'my-programs'
  | 'videos'
  | 'progress'
  | 'challenges'
  | 'highlights'
  | 'settings'
  | 'family';

// Proxy mode athlete info (when guardian acts as a managed athlete)
interface ProxyAthlete {
  athlete_id: string;
  athlete_name: string;
  entered_at: number; // Timestamp when proxy mode was entered (for session timeout)
}

// Proxy session timeout (1 hour)
const PROXY_SESSION_TIMEOUT_MS = 60 * 60 * 1000;

// Athlete navigation items
const ATHLETE_NAV_ITEMS: { page: Page; label: string; icon: typeof Home }[] = [
  { page: 'home', label: 'Dashboard', icon: Home },
  { page: 'my-programs', label: 'My Programs', icon: BookOpen },
  { page: 'videos', label: 'My Videos', icon: Video },
  { page: 'progress', label: 'My Progress', icon: BarChart3 },
  { page: 'challenges', label: 'Challenges', icon: Trophy },
  { page: 'highlights', label: 'Highlights', icon: Film },
  { page: 'settings', label: 'Settings', icon: Settings },
];

// Guardian navigation items (simplified for parent/guardian view)
const GUARDIAN_NAV_ITEMS: { page: Page; label: string; icon: typeof Home }[] = [
  { page: 'home', label: 'Dashboard', icon: Home },
  { page: 'videos', label: 'Videos', icon: Video },
  { page: 'family', label: 'My Athletes', icon: Users },
  { page: 'settings', label: 'Settings', icon: Settings },
];

function App() {
  const { authState, logout } = useAuth();
  const { userProfile, loadUserProfile } = useUser();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Organization state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);

  // Coach redirect state (coaches/owners should use coach portal)
  const [redirectingToCoachPortal, setRedirectingToCoachPortal] = useState(false);

  // Onboarding chat state
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Pending invite state - allows signup even when signupEnabled=false
  const [pendingInviteToken, setPendingInviteToken] = useState<string | null>(() => {
    return localStorage.getItem('pendingInviteToken');
  });
  const [validatedInvite, setValidatedInvite] = useState<ValidatedInvite | null>(null);

  // Pending team code state - for QR code signups (no email validation needed)
  const [pendingTeamCode, setPendingTeamCode] = useState<string | null>(() => {
    return localStorage.getItem('pendingTeamCode');
  });

  // Proxy mode state - when guardian acts as a managed athlete
  // Uses sessionStorage (clears on tab close) for security
  const [proxyAthlete, setProxyAthlete] = useState<ProxyAthlete | null>(() => {
    const stored = sessionStorage.getItem('proxyAthlete');
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored);
      // Check if proxy session has expired (1 hour timeout)
      if (parsed.entered_at && Date.now() - parsed.entered_at > PROXY_SESSION_TIMEOUT_MS) {
        console.log('[Proxy] Session expired, clearing proxy state');
        sessionStorage.removeItem('proxyAthlete');
        return null;
      }
      return parsed;
    } catch {
      sessionStorage.removeItem('proxyAthlete');
      return null;
    }
  });

  // Detect guardian role
  const isGuardian = currentOrg?.role === 'GUARDIAN';

  // In proxy mode, guardian acts as athlete
  const isInProxyMode = isGuardian && proxyAthlete !== null;

  // Get navigation items based on role (proxy mode uses athlete nav)
  const navItems = useMemo(() => {
    if (isInProxyMode) return ATHLETE_NAV_ITEMS;
    return isGuardian ? GUARDIAN_NAV_ITEMS : ATHLETE_NAV_ITEMS;
  }, [isGuardian, isInProxyMode]);

  // Functions to enter/exit proxy mode
  const enterProxyMode = useCallback(
    (athlete: { athlete_id: string; athlete_name: string }) => {
      const proxyData: ProxyAthlete = {
        ...athlete,
        entered_at: Date.now(),
      };
      setProxyAthlete(proxyData);
      sessionStorage.setItem('proxyAthlete', JSON.stringify(proxyData));
      // Audit log: proxy mode entered
      console.log('[Proxy Audit] Entered proxy mode', {
        athlete_id: athlete.athlete_id,
        athlete_name: athlete.athlete_name,
        timestamp: new Date().toISOString(),
      });
      navigate('/');
    },
    [navigate]
  );

  const exitProxyMode = useCallback(() => {
    // Audit log: proxy mode exited
    if (proxyAthlete) {
      console.log('[Proxy Audit] Exited proxy mode', {
        athlete_id: proxyAthlete.athlete_id,
        athlete_name: proxyAthlete.athlete_name,
        timestamp: new Date().toISOString(),
      });
    }
    setProxyAthlete(null);
    sessionStorage.removeItem('proxyAthlete');
    navigate('/family');
  }, [navigate, proxyAthlete]);

  // Get current page from URL
  const getCurrentPage = useCallback((pathname: string): Page => {
    if (pathname === '/' || pathname === '/home') return 'home';
    if (pathname.startsWith('/my-programs')) return 'my-programs';
    if (pathname.startsWith('/videos')) return 'videos';
    if (pathname.startsWith('/progress')) return 'progress';
    if (pathname.startsWith('/challenges')) return 'challenges';
    if (pathname.startsWith('/highlights')) return 'highlights';
    if (pathname.startsWith('/family')) return 'family';
    if (pathname.startsWith('/settings')) return 'settings';
    return 'home';
  }, []);

  const currentPage = getCurrentPage(location.pathname);

  // Load user data on auth
  useEffect(() => {
    if (authState === 'authenticated') {
      loadUserProfile();
      loadOrganizations();
    }
  }, [authState, loadUserProfile]);

  // Check if onboarding should be shown after profile loads
  // Note: Guardians skip the athlete onboarding - they don't need training profiles
  useEffect(() => {
    if (authState !== 'authenticated' || !userProfile) return;

    // Wait for org to load before checking - otherwise isGuardian may be wrong
    if (!currentOrg) return;

    // Guardians don't need athlete onboarding
    if (isGuardian) {
      setShowOnboarding(false);
      return;
    }

    // Check if user has completed key profile fields
    const hasCompletedProfile = Boolean(
      userProfile.learning_style && userProfile.motivation_type && userProfile.position
    );

    // Check if user has skipped onboarding before
    const hasSkippedOnboarding = localStorage.getItem('onboardingSkipped') === 'true';

    // Show onboarding if profile is incomplete and not skipped
    if (!hasCompletedProfile && !hasSkippedOnboarding) {
      setShowOnboarding(true);
    }
  }, [authState, userProfile, isGuardian, currentOrg]);

  // Verify family link is still valid when in proxy mode on startup
  useEffect(() => {
    async function verifyFamilyLink() {
      if (!isGuardian || !proxyAthlete || !currentOrg) return;

      try {
        const { links } = await listFamilyLinks(currentOrg.org_id);
        const validLink = links?.find(
          (link) =>
            link.athlete_id === proxyAthlete.athlete_id &&
            link.status === 'APPROVED' &&
            link.is_guardian_managed
        );

        if (!validLink) {
          // Family link no longer valid - exit proxy mode
          console.log('[Proxy Audit] Family link verification failed, exiting proxy mode', {
            athlete_id: proxyAthlete.athlete_id,
            timestamp: new Date().toISOString(),
          });
          setProxyAthlete(null);
          sessionStorage.removeItem('proxyAthlete');
        } else {
          console.log('[Proxy] Family link verified for', proxyAthlete.athlete_name);
        }
      } catch (err) {
        // On error, don't auto-logout - could be network issue
        console.error('[Proxy] Failed to verify family link:', err);
      }
    }

    verifyFamilyLink();
  }, [isGuardian, proxyAthlete?.athlete_id, currentOrg?.org_id]);

  // Handle org selection with localStorage persistence
  const handleSelectOrg = useCallback((org: Organization) => {
    setCurrentOrg(org);
    localStorage.setItem('lastSelectedOrgId', org.org_id);
  }, []);

  async function loadOrganizations() {
    try {
      const { orgs } = await listOrgs();
      setOrganizations(orgs);
      if (orgs.length > 0) {
        // Priority 1: Check for org param in URL (from cross-app redirect)
        const params = new URLSearchParams(window.location.search);
        const orgIdFromUrl = params.get('org');

        if (orgIdFromUrl) {
          const matchingOrg = orgs.find((o) => o.org_id === orgIdFromUrl);
          if (matchingOrg) {
            setCurrentOrg(matchingOrg);
            localStorage.setItem('lastSelectedOrgId', matchingOrg.org_id);
            // Clear the org param from URL without reload
            params.delete('org');
            const newUrl = params.toString()
              ? `${window.location.pathname}?${params.toString()}`
              : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            return;
          }
        }

        // Priority 2: Restore last selected org from localStorage
        const storedOrgId = localStorage.getItem('lastSelectedOrgId');
        if (storedOrgId) {
          const storedOrg = orgs.find((o) => o.org_id === storedOrgId);
          if (storedOrg) {
            setCurrentOrg(storedOrg);
            return;
          }
          // Stored org no longer valid (user removed from org) - clear it
          localStorage.removeItem('lastSelectedOrgId');
        }

        // Priority 3: Default to first org
        setCurrentOrg(orgs[0]);
        localStorage.setItem('lastSelectedOrgId', orgs[0].org_id);
      }
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  }

  // Detect and validate invite token from URL (runs once on mount)
  useEffect(() => {
    async function detectAndValidateInvite() {
      const params = new URLSearchParams(window.location.search);
      const inviteTokenFromUrl = params.get('invite');

      // Use token from URL or from localStorage
      const tokenToValidate = inviteTokenFromUrl || pendingInviteToken;

      if (!tokenToValidate) return;

      // Store in localStorage if from URL (persists across signup flow)
      if (inviteTokenFromUrl) {
        localStorage.setItem('pendingInviteToken', inviteTokenFromUrl);
        setPendingInviteToken(inviteTokenFromUrl);
        // Clear URL param but keep the token stored
        window.history.replaceState({}, '', window.location.pathname);
      }

      // Validate the invite (public endpoint, no auth required)
      try {
        const validation = await validateInvite(tokenToValidate);
        if (validation.valid && validation.invite) {
          setValidatedInvite(validation.invite);
        } else {
          // Invalid invite - clear stored token
          localStorage.removeItem('pendingInviteToken');
          setPendingInviteToken(null);
          setValidatedInvite(null);
          if (validation.reason === 'expired') {
            showError('This invite link has expired. Please ask for a new invite.');
          } else if (validation.reason === 'used') {
            showError('This invite link has already been used.');
          }
        }
      } catch {
        // Silently fail - invite validation is non-critical
      }
    }
    detectAndValidateInvite();
  }, []); // Only run once on mount

  // Detect team code from URL (for QR code signups - runs once on mount)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const teamCodeFromUrl = params.get('team_code');

    if (teamCodeFromUrl) {
      // Store in localStorage (persists across signup flow)
      const normalizedCode = teamCodeFromUrl.trim().toUpperCase();
      localStorage.setItem('pendingTeamCode', normalizedCode);
      setPendingTeamCode(normalizedCode);
      // Clear URL param but keep the code stored
      params.delete('team_code');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      console.log('[TeamCode] Detected and stored team code:', normalizedCode);
    }
  }, []); // Only run once on mount

  // Auto-join team after user logs in (via QR code)
  useEffect(() => {
    async function joinPendingTeam() {
      if (authState !== 'authenticated') return;
      // Wait for user profile to be loaded (ensures user is fully initialized)
      if (!userProfile) return;

      // Get team code from state or localStorage (in case state was cleared)
      const teamCode = pendingTeamCode || localStorage.getItem('pendingTeamCode');
      if (!teamCode || !teamCode.trim()) {
        return;
      }

      console.log('[TeamCode] Attempting to join team with code:', teamCode);

      try {
        const result = await joinViaTeamCode(teamCode.trim().toUpperCase());
        // Clear the stored team code
        localStorage.removeItem('pendingTeamCode');
        setPendingTeamCode(null);
        // Reload orgs to get the new membership
        await loadOrganizations();

        // Show success message with trial info if applicable
        if (result.trial?.granted) {
          showSuccess(
            `Welcome to ${result.team.name}! You've been granted a ${result.trial.trial_days}-day Pro trial.`
          );
        } else {
          showSuccess(`Successfully joined ${result.team.name}!`);
        }
      } catch (err: any) {
        // Clear the stored team code even on error
        localStorage.removeItem('pendingTeamCode');
        setPendingTeamCode(null);
        console.error('[TeamCode] Join failed:', err);
        showError(err.message || 'Failed to join team. The invite code may be invalid or expired.');
      }
    }
    joinPendingTeam();
  }, [authState, pendingTeamCode, userProfile]);

  // Auto-accept invite after user logs in
  useEffect(() => {
    async function acceptPendingInvite() {
      if (authState !== 'authenticated' || !pendingInviteToken) return;
      // Wait for user profile to load before checking email match
      if (!userProfile) return;
      // Wait for invite validation to complete (validatedInvite will be set if valid)
      // This prevents race conditions where accept runs before validation finishes
      if (!validatedInvite) return;

      // Check if user's email matches the invite email before attempting to accept
      // This prevents the 403 error that occurs when logging in with a different email
      // Skip check if profile email is empty (backend will populate it from invite)
      if (
        userProfile.email &&
        userProfile.email.trim() &&
        userProfile.email.toLowerCase() !== validatedInvite.email.toLowerCase()
      ) {
        // Clear the invite - it was meant for a different email address
        localStorage.removeItem('pendingInviteToken');
        setPendingInviteToken(null);
        setValidatedInvite(null);
        showError(
          `This invite was sent to ${validatedInvite.email}. Please sign in with that email address, or ask for a new invite to ${userProfile.email}.`
        );
        return;
      }

      try {
        // Handle family invites differently from email invites
        if (validatedInvite.invite_type === 'FAMILY') {
          // Family invite - use family accept endpoint
          await acceptFamilyInvite(validatedInvite.org_id, pendingInviteToken);
          // Clear the stored invite token
          localStorage.removeItem('pendingInviteToken');
          setPendingInviteToken(null);
          setValidatedInvite(null);
          // Reload orgs to get the new membership as GUARDIAN
          await loadOrganizations();
          showSuccess(`Successfully linked to ${validatedInvite.athlete_name || 'your athlete'}!`);
        } else {
          // Email invite (athlete invite) - use standard accept endpoint
          const result = await acceptEmailInvite(pendingInviteToken);
          // Clear the stored invite token
          localStorage.removeItem('pendingInviteToken');
          setPendingInviteToken(null);
          setValidatedInvite(null);
          // Reload orgs to get the new membership
          await loadOrganizations();
          showSuccess(`Successfully joined ${result.org.name}!`);
        }
      } catch (err: any) {
        // Clear the stored invite token even on error
        localStorage.removeItem('pendingInviteToken');
        setPendingInviteToken(null);
        setValidatedInvite(null);
        showError(err.message || 'Failed to accept invite');
      }
    }
    acceptPendingInvite();
  }, [authState, pendingInviteToken, userProfile, validatedInvite]);

  // Route guard: Redirect coaches/owners to coach portal
  // This is the athlete portal - coaches and owners should use the coach portal
  useEffect(() => {
    const isCoachOrOwner =
      currentOrg?.role === 'COACH' ||
      currentOrg?.role === 'OWNER' ||
      currentOrg?.role === 'MODERATOR';
    if (isCoachOrOwner && config.crossApp.coachPortalUrl) {
      // Show redirect screen, then redirect after a brief delay
      setRedirectingToCoachPortal(true);
      const timer = setTimeout(() => {
        // Pass org context to prevent redirect loops for multi-org users
        const redirectUrl = new URL(config.crossApp.coachPortalUrl);
        redirectUrl.searchParams.set('org', currentOrg.org_id);
        window.location.href = redirectUrl.toString();
      }, 2000); // 2 second delay for user to read the message
      return () => clearTimeout(timer);
    }
  }, [currentOrg?.role]);

  function handleNavigate(page: Page) {
    navigate(`/${page === 'home' ? '' : page}`);
    setMobileMenuOpen(false);
  }

  async function handleLogout() {
    // Audit log: logout (clears proxy mode if active)
    if (proxyAthlete) {
      console.log('[Proxy Audit] Logout while in proxy mode', {
        athlete_id: proxyAthlete.athlete_id,
        timestamp: new Date().toISOString(),
      });
    }
    // Clear proxy mode state on logout
    setProxyAthlete(null);
    sessionStorage.removeItem('proxyAthlete');
    // Clear org selection to prevent data leakage to next user
    localStorage.removeItem('lastSelectedOrgId');
    await logout();
    navigate('/');
  }

  function handleOnboardingComplete() {
    setShowOnboarding(false);
    loadUserProfile(); // Refresh profile with new data
  }

  function handleOnboardingSkip() {
    localStorage.setItem('onboardingSkipped', 'true');
    setShowOnboarding(false);
  }

  // Sidebar content component (shared between desktop and mobile)
  const SidebarContent = ({ isCollapsed = false }: { isCollapsed?: boolean }) => (
    <>
      {/* Logo block */}
      <div
        style={{
          padding: isCollapsed ? '20px 12px' : '20px 24px',
          borderBottom: '1px solid rgba(var(--purple-rgb), 0.2)',
          position: 'relative',
        }}
      >
        <button
          data-mobile-only
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            padding: '8px',
            color: 'white',
            cursor: 'pointer',
          }}
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
        <div
          onClick={() => handleNavigate('home')}
          style={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}
          title="Go to home"
        >
          <img
            src="/logo.png"
            alt="Skool365"
            style={{
              width: isCollapsed ? '40px' : '140px',
              height: isCollapsed ? '40px' : '140px',
              objectFit: 'contain',
              transition: 'all 0.2s ease',
            }}
          />
        </div>
      </div>

      {/* Collapse toggle — desktop only */}
      <button
        data-desktop-only
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-end',
          gap: '8px',
          padding: isCollapsed ? '12px' : '12px 24px',
          cursor: 'pointer',
          background: 'transparent',
          border: 'none',
          color: 'var(--sidebar-muted)',
          transition: 'all 0.2s ease',
          width: '100%',
        }}
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        {!isCollapsed && <span style={{ fontSize: '12px' }}>Collapse</span>}
      </button>

      {/* Proxy mode indicator — single block with conditional values */}
      {isInProxyMode && (
        <div
          style={{
            margin: isCollapsed ? '0 8px 12px' : '0 12px 12px',
            padding: isCollapsed ? '8px' : '12px',
            background: isCollapsed
              ? 'rgba(var(--green-rgb), 0.15)'
              : 'linear-gradient(135deg, rgba(var(--green-rgb), 0.15) 0%, rgba(var(--green-rgb), 0.10) 100%)',
            border: isCollapsed ? 'none' : '1px solid rgba(var(--green-rgb), 0.3)',
            borderRadius: '8px',
            display: isCollapsed ? 'flex' : 'block',
            justifyContent: isCollapsed ? 'center' : undefined,
          }}
          title={isCollapsed ? `Acting as ${proxyAthlete?.athlete_name}` : undefined}
        >
          {isCollapsed ? (
            <UserCheck size={18} style={{ color: 'var(--color-success)' }} />
          ) : (
            <>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}
              >
                <UserCheck size={16} style={{ color: 'var(--color-success)' }} />
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#047857',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Acting as
                </span>
              </div>
              <p
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--gray-900)',
                  marginBottom: '8px',
                }}
              >
                {proxyAthlete?.athlete_name}
              </p>
              <button
                onClick={exitProxyMode}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '12px',
                  background: 'white',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: 'var(--gray-600)',
                  fontWeight: 500,
                }}
              >
                Switch Athlete
              </button>
            </>
          )}
        </div>
      )}

      {/* Org switcher */}
      <OrgSwitcher
        organizations={organizations}
        currentOrg={currentOrg}
        onSelectOrg={handleSelectOrg}
        variant={isCollapsed ? 'collapsed' : 'sidebar'}
      />

      {/* Navigation — div onClick mirroring coach */}
      <nav style={{ flex: 1, padding: '16px 0', overflowY: 'auto' }}>
        {navItems.map(({ page, label, icon: Icon }) => {
          const isActive = currentPage === page;
          return (
            <div
              key={page}
              onClick={() => handleNavigate(page)}
              title={isCollapsed ? label : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                gap: '12px',
                padding: isCollapsed ? '14px 12px' : '14px 24px',
                cursor: 'pointer',
                background: isActive ? 'rgba(var(--purple-rgb), 0.2)' : 'transparent',
                borderLeft: isActive ? '3px solid var(--purple-600)' : '3px solid transparent',
                transition: 'all 0.2s ease',
                color: isActive ? 'white' : 'var(--sidebar-muted)',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  e.currentTarget.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--sidebar-muted)';
                }
              }}
            >
              <Icon size={20} />
              {!isCollapsed && <span style={{ fontWeight: isActive ? 600 : 500 }}>{label}</span>}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div
          onClick={handleLogout}
          title={isCollapsed ? 'Logout' : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: '12px',
            padding: isCollapsed ? '14px 12px' : '14px 24px',
            cursor: 'pointer',
            color: 'var(--sidebar-muted)',
            transition: 'all 0.2s ease',
          }}
        >
          <LogOut size={20} />
          {!isCollapsed && <span style={{ fontWeight: 500 }}>Logout</span>}
        </div>
      </div>
    </>
  );

  // Show loading state
  if (authState === 'loading') {
    return (
      <div className="fullscreen-center" style={{ background: 'var(--page-bg)' }}>
        <div
          className="animate-spin"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: '3px solid var(--gray-200)',
            borderTopColor: 'var(--purple-600)',
          }}
        />
      </div>
    );
  }

  // Show auth modal for unauthenticated users
  if (authState !== 'authenticated') {
    // Marketing-site CTAs (e.g. the trial link on skool365.ai) drop visitors
    // here with `?signup=1`. Open the modal directly in register mode.
    const wantsSignup =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('signup') === '1';
    return (
      <AuthModal
        onClose={() => {}}
        validatedInvite={validatedInvite}
        pendingTeamCode={pendingTeamCode}
        initialMode={wantsSignup ? 'register' : undefined}
      />
    );
  }

  // Show onboarding chat for new athletes with incomplete profiles
  const displayName = userProfile?.given_name || userProfile?.email?.split('@')[0] || '';

  // Coach/Owner redirect screen - shown before redirecting to coach portal
  if (redirectingToCoachPortal && config.crossApp.coachPortalUrl) {
    return (
      <div className="fullscreen-center" style={{ background: 'var(--page-bg)', padding: '20px' }}>
        <div style={{ textAlign: 'center', color: 'var(--gray-900)', maxWidth: '400px' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--purple-600) 0%, #a855f7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              color: 'white',
            }}
          >
            <ArrowRight size={40} />
          </div>
          <h1 style={{ fontSize: '24px', marginBottom: '12px', fontWeight: 600 }}>
            Redirecting to Coach Portal
          </h1>
          <p style={{ color: 'var(--gray-600)', marginBottom: '24px', lineHeight: 1.6 }}>
            This is the athlete portal. As a coach, you have access to the full coach dashboard
            where you can manage your team, create programs, and review athlete videos.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              color: 'var(--gray-500)',
            }}
          >
            <Loader2 size={16} className="animate-spin" />
            <span style={{ fontSize: '14px' }}>Taking you there now...</span>
          </div>
          <button
            onClick={() => {
              const redirectUrl = new URL(config.crossApp.coachPortalUrl);
              if (currentOrg) redirectUrl.searchParams.set('org', currentOrg.org_id);
              window.location.href = redirectUrl.toString();
            }}
            style={{
              marginTop: '32px',
              padding: '12px 24px',
              background: 'var(--purple-600)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
          >
            Go Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      {/* Onboarding chat for new users */}
      {showOnboarding && (
        <OnboardingChat
          userName={displayName}
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Mobile menu overlay */}
      <div
        className={`sidebar-overlay ${mobileMenuOpen ? 'visible' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Sidebar */}
      <aside
        className={`sidebar ${mobileMenuOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}
      >
        <SidebarContent isCollapsed={sidebarCollapsed} />
      </aside>

      {/* Main content */}
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Mobile header */}
        <div className="mobile-header" style={{ display: 'none' }}>
          <button onClick={() => setMobileMenuOpen(true)} className="mobile-menu-btn">
            <Menu size={24} />
          </button>
          <div className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Skool365"
              style={{ width: '32px', height: '32px', objectFit: 'contain' }}
            />
            <span style={{ fontWeight: '600', color: 'var(--gray-900)' }}>Skool365</span>
          </div>
          <OrgSwitcher
            organizations={organizations}
            currentOrg={currentOrg}
            onSelectOrg={handleSelectOrg}
            variant="mobile"
          />
        </div>

        {/* Page content */}
        <div>
          <Routes>
            {/* Home page - different for guardians vs athletes (proxy mode acts as athlete) */}
            <Route
              path="/"
              element={
                isInProxyMode ? (
                  <HomePage
                    org={currentOrg}
                    proxyAthleteId={proxyAthlete?.athlete_id}
                    proxyAthleteName={proxyAthlete?.athlete_name}
                  />
                ) : isGuardian ? (
                  <GuardianHomePage org={currentOrg} />
                ) : (
                  <HomePage org={currentOrg} />
                )
              }
            />
            <Route
              path="/home"
              element={
                isInProxyMode ? (
                  <HomePage
                    org={currentOrg}
                    proxyAthleteId={proxyAthlete?.athlete_id}
                    proxyAthleteName={proxyAthlete?.athlete_name}
                  />
                ) : isGuardian ? (
                  <GuardianHomePage org={currentOrg} />
                ) : (
                  <HomePage org={currentOrg} />
                )
              }
            />

            {/* Athlete pages - available to athletes and guardians in proxy mode */}
            {(!isGuardian || isInProxyMode) && (
              <>
                <Route
                  path="/my-programs/*"
                  element={
                    <MyProgramsPage org={currentOrg} proxyAthleteId={proxyAthlete?.athlete_id} />
                  }
                />
                <Route
                  path="/progress/*"
                  element={
                    <ProgressPage org={currentOrg} proxyAthleteId={proxyAthlete?.athlete_id} />
                  }
                />
                <Route
                  path="/challenges/*"
                  element={
                    <ChallengesPage org={currentOrg} proxyAthleteId={proxyAthlete?.athlete_id} />
                  }
                />
                <Route
                  path="/highlights/*"
                  element={
                    <HighlightsPage org={currentOrg} proxyAthleteId={proxyAthlete?.athlete_id} />
                  }
                />
              </>
            )}

            {/* Shared pages */}
            <Route
              path="/videos/*"
              element={
                <VideosPage
                  org={currentOrg}
                  isGuardian={isGuardian && !isInProxyMode}
                  proxyAthleteId={proxyAthlete?.athlete_id}
                />
              }
            />
            <Route
              path="/settings/*"
              element={
                <SettingsPage
                  isGuardian={isGuardian}
                  organizations={organizations}
                  currentOrg={currentOrg}
                  onSelectOrg={handleSelectOrg}
                />
              }
            />

            {/* Family page - guardians can always access to switch athletes */}
            {isGuardian && (
              <Route
                path="/family/*"
                element={
                  <FamilyPage
                    org={currentOrg}
                    onSelectAthlete={enterProxyMode}
                    proxyAthlete={proxyAthlete}
                    onExitProxyMode={exitProxyMode}
                  />
                }
              />
            )}

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>

      {/* Inline styles for responsive behavior */}
      <style>{`
        @media (max-width: 768px) {
          .mobile-header { display: flex !important; }
          [data-desktop-only] { display: none !important; }
        }
        @media (min-width: 769px) {
          [data-mobile-only] { display: none !important; }
          [data-desktop-only] { display: flex !important; }
          .sidebar { transform: translateX(0) !important; }
        }
      `}</style>
    </div>
  );
}

export default App;
